import { useState, type MouseEvent, type ReactNode } from "react";
import { ChevronLeft, ChevronRight, Heart, Pencil } from "lucide-react";
import type { Product, ProductImage } from "../../../lib/api";
import { formatMoney } from "../../../lib/money";
import { useShop, useWishlist } from "../../../stores/cart";
import { useStorefrontContext } from "./StorefrontContext";
import { useCarousel } from "./useCarousel";

export function isSoldOut(product: Product): boolean {
  return product.stock !== null && product.stock <= 0;
}

/** Price with the original struck through when on sale. */
export function Price({ product, large = false }: { product: Product; large?: boolean }) {
  const { store } = useStorefrontContext();
  const onSale = product.compareAtMinor !== null && product.compareAtMinor > product.priceMinor;
  return (
    <span className={large ? "zs-price zs-price--lg" : "zs-price"}>
      <span className={onSale ? "zs-price__now zs-price__now--sale" : "zs-price__now"}>
        {formatMoney(product.priceMinor, store.currency)}
      </span>
      {onSale ? (
        <s className="zs-price__was">{formatMoney(product.compareAtMinor!, store.currency)}</s>
      ) : null}
    </span>
  );
}

export function WishlistButton({ product, variant = "floating" }: { product: Product; variant?: "floating" | "inline" }) {
  const { slug, interactive } = useStorefrontContext();
  const saved = useWishlist(slug).includes(product.id);
  const toggle = useShop((s) => s.toggleWishlist);
  const label = saved ? `Remove ${product.name} from wishlist` : `Save ${product.name} to wishlist`;
  return (
    <button
      type="button"
      className={`zs-wish zs-wish--${variant}${saved ? " zs-wish--on" : ""}`}
      aria-label={label}
      aria-pressed={saved}
      title={saved ? "Saved" : "Save for later"}
      onClick={(e: MouseEvent) => {
        e.preventDefault();
        if (!interactive) return;
        e.stopPropagation();
        toggle(slug, product.id);
      }}
    >
      <Heart size={variant === "inline" ? 18 : 16} aria-hidden="true" />
      {variant === "inline" ? <span>{saved ? "Saved" : "Save"}</span> : null}
    </button>
  );
}

/** Hovering a card steps through its photos at this pace. */
const CARD_HOVER_MS = 1400;

/**
 * A card's photo frame. Every card in the grid shares the store's shape; each
 * photo is shown whole inside it, over a soft blurred copy instead of bare
 * bars. All photos are reachable: by hovering, the arrows, or swiping.
 */
function CardPhotos({
  images,
  name,
  shape,
  interactive,
  children,
}: {
  images: ProductImage[];
  name: string;
  shape: string;
  interactive: boolean;
  children: ReactNode;
}) {
  const [hovered, setHovered] = useState(false);
  const carousel = useCarousel(images.length, { autoplayMs: CARD_HOVER_MS, paused: !hovered });
  const many = images.length > 1;

  const step = (e: MouseEvent, by: number) => {
    e.preventDefault();
    e.stopPropagation();
    carousel.show(carousel.index + by);
  };

  return (
    <div
      className={`zs-card__media zs-card__media--${shape}`}
      onMouseEnter={() => {
        setHovered(true);
        // Show the next photo straight away, as a hint that there are more.
        if (many) carousel.show(1, false);
      }}
      onMouseLeave={() => {
        setHovered(false);
        carousel.reset();
      }}
      {...(many ? carousel.swipeHandlers : {})}
    >
      {images.length === 0 ? <div className="zs-card__placeholder" aria-hidden="true" /> : null}
      {images.map((img, i) => (
        <div
          key={`${img.url}-${i}`}
          className="zs-photo"
          data-active={i === carousel.index ? "true" : undefined}
          aria-hidden={i === carousel.index ? undefined : true}
        >
          <img className="zs-photo__backdrop" src={img.url} alt="" loading="lazy" decoding="async" />
          <img
            className="zs-photo__img"
            src={img.url}
            alt={i === carousel.index ? img.alt || name : ""}
            loading="lazy"
            decoding="async"
            draggable={false}
          />
        </div>
      ))}
      {many ? (
        <>
          {interactive ? (
            <>
              <button type="button" className="zs-card__nav zs-card__nav--prev" aria-label="Previous photo" onClick={(e) => step(e, -1)}>
                <ChevronLeft size={16} aria-hidden="true" />
              </button>
              <button type="button" className="zs-card__nav zs-card__nav--next" aria-label="Next photo" onClick={(e) => step(e, 1)}>
                <ChevronRight size={16} aria-hidden="true" />
              </button>
            </>
          ) : null}
          <span className="zs-dots" aria-label={`Photo ${carousel.index + 1} of ${images.length}`} role="img">
            {images.map((img, i) => (
              <i key={`${img.url}-${i}`} data-active={i === carousel.index ? "true" : undefined} />
            ))}
          </span>
        </>
      ) : null}
      {children}
    </div>
  );
}

export function ProductCard({ product }: { product: Product }) {
  const { store, paths, go, interactive, onEditProduct } = useStorefrontContext();
  // In the editor, cards are part of a selectable block, not links.
  const interactiveTabIndex = interactive ? undefined : -1;
  const href = paths.product(product);
  const onSale = product.compareAtMinor !== null && product.compareAtMinor > product.priceMinor;
  const soldOut = isSoldOut(product);

  return (
    <article className="zs-card">
      <a
        className="zs-card__link"
        href={href}
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey) return;
          e.preventDefault();
          go(href);
        }}
        tabIndex={interactiveTabIndex}
      >
        <CardPhotos images={product.images} name={product.name} shape={store.cardShape ?? "portrait"} interactive={interactive}>
          {soldOut ? (
            <span className="zs-tag zs-tag--muted">Sold out</span>
          ) : onSale ? (
            <span className="zs-tag">Sale</span>
          ) : null}
          {onEditProduct && !interactive ? (
            <button
              type="button"
              className="zs-card__edit"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEditProduct(product);
              }}
            >
              <Pencil size={13} aria-hidden="true" />
              Edit product
            </button>
          ) : null}
        </CardPhotos>
        <div className="zs-card__body">
          {product.collection ? <span className="zs-card__collection">{product.collection}</span> : null}
          <h3 className="zs-card__name">{product.name}</h3>
          <Price product={product} />
        </div>
      </a>
    </article>
  );
}
